import React, { useState } from 'react';
import { X, Pencil } from 'lucide-react';
import Select from 'react-select';

const FIELD_OPTIONS = [
  { value: 'industry', label: 'Industry' },
  { value: 'leadStatus', label: 'Lead Status' },
  { value: 'leadSource', label: 'Lead Source' },
  { value: 'relationshipType', label: 'Relationship Type' },
  { value: 'companyStage', label: 'Company Stage' },
  { value: 'legalType', label: 'Legal Type' },
  { value: 'employeeCount', label: 'Employee Count' },
  { value: 'annualRevenue', label: 'Annual Revenue' },
  { value: 'paymentTerms', label: 'Payment Terms' },
];

// ⚠️ Values must match Java SalesEnums exactly (case-sensitive)
const FIELD_VALUES = {
  industry: [
    { value: 'IT', label: 'IT' },
    { value: 'FINANCE', label: 'Finance' },
    { value: 'HEALTHCARE', label: 'Healthcare' },
    { value: 'EDUCATION', label: 'Education' },
    { value: 'MANUFACTURING', label: 'Manufacturing' },
    { value: 'RETAIL', label: 'Retail' },
    { value: 'OTHER', label: 'Other' },
  ],
  leadStatus: [
    { value: 'NEW', label: 'New' },
    { value: 'CONTACTED', label: 'Contacted' },
    { value: 'QUALIFIED', label: 'Qualified' },
    { value: 'LOST', label: 'Lost' },
    { value: 'WON', label: 'Won' },
    { value: 'OTHER', label: 'Other' },
  ],
  leadSource: [
    { value: 'WEBSITE', label: 'Website' },
    { value: 'REFERRAL', label: 'Referral' },
    { value: 'PARTNER', label: 'Partner' },
    { value: 'SOCIAL_MEDIA', label: 'Social Media' },
    { value: 'EMAIL', label: 'Email' },
    { value: 'CALL', label: 'Call' },
    { value: 'OTHER', label: 'Other' },
  ],
  relationshipType: [
    { value: 'CLIENT', label: 'Client' },
    { value: 'PARTNER', label: 'Partner' },
    { value: 'SUPPLIER', label: 'Supplier' },
    { value: 'VENDOR', label: 'Vendor' },
    { value: 'DISTRIBUTOR', label: 'Distributor' },
    { value: 'RESELLER', label: 'Reseller' },
    { value: 'CONTRACTOR', label: 'Contractor' },
    { value: 'CONSULTANT', label: 'Consultant' },
    { value: 'SERVICE_PROVIDER', label: 'Service Provider' },
    { value: 'INVESTOR', label: 'Investor' },
    { value: 'MANUFACTURER', label: 'Manufacturer' },
    { value: 'OTHER', label: 'Other' },
  ],
  companyStage: [
    { value: 'STARTUP', label: 'Startup' },
    { value: 'SMALL', label: 'Small' },
    { value: 'MEDIUM', label: 'Medium' },
    { value: 'LARGE', label: 'Large' },
    { value: 'ENTERPRISE', label: 'Enterprise' },
    { value: 'OTHER', label: 'Other' },
  ],
  legalType: [
    { value: 'SOLE_PROPRIETORSHIP', label: 'Sole Proprietorship' },
    { value: 'PARTNERSHIP_FIRM', label: 'Partnership Firm' },
    { value: 'LLP', label: 'LLP' },
    { value: 'PRIVATE_LIMITED', label: 'Private Limited' },
    { value: 'PUBLIC_LIMITED', label: 'Public Limited' },
    { value: 'OPC', label: 'OPC' },
    { value: 'JOINT_VENTURE', label: 'Joint Venture' },
    { value: 'NON_PROFIT', label: 'Non-Profit' },
    { value: 'GOVERNMENT_ENTITY', label: 'Government Entity' },
    { value: 'OTHER', label: 'Other' },
  ],
  employeeCount: [
    { value: 'RANGE_1_10', label: '1–10' },
    { value: 'RANGE_11_50', label: '11–50' },
    { value: 'RANGE_51_100', label: '51–100' },
    { value: 'RANGE_101_250', label: '101–250' },
    { value: 'RANGE_251_500', label: '251–500' },
    { value: 'RANGE_501_1000', label: '501–1000' },
    { value: 'RANGE_1001_5000', label: '1,001–5,000' },
    { value: 'RANGE_5001_10000', label: '5,001–10,000' },
    { value: 'RANGE_10000_PLUS', label: '10,000+' },
  ],
  annualRevenue: [
    { value: 'BELOW_1M', label: 'Below $1M' },
    { value: 'RANGE_1M_5M', label: '$1M – $5M' },
    { value: 'RANGE_5M_10M', label: '$5M – $10M' },
    { value: 'RANGE_10M_50M', label: '$10M – $50M' },
    { value: 'RANGE_50M_100M', label: '$50M – $100M' },
    { value: 'RANGE_100M_500M', label: '$100M – $500M' },
    { value: 'RANGE_500M_1B', label: '$500M – $1B' },
    { value: 'RANGE_1B_5B', label: '$1B – $5B' },
    { value: 'RANGE_5B_10B', label: '$5B – $10B' },
    { value: 'ABOVE_10B', label: 'Above $10B' },
  ],
  paymentTerms: [
    { value: 'NET15', label: 'Net 15' },
    { value: 'NET30', label: 'Net 30' },
    { value: 'NET45', label: 'Net 45' },
    { value: 'NET60', label: 'Net 60' },
    { value: 'NET90', label: 'Net 90' },
    { value: 'COD', label: 'Cash on Delivery' },
    { value: 'PREPAID', label: 'Prepaid' },
    { value: 'LC', label: 'Letter of Credit' },
    { value: 'PAYMENT_ON_RECEIPT', label: 'Payment on Receipt' },
    { value: 'MONTHLY', label: 'Monthly' },
    { value: 'QUARTERLY', label: 'Quarterly' },
    { value: 'ANNUAL', label: 'Annual' },
    { value: 'OTHER', label: 'Other' },
  ],
};

// ✅ Address fields mapped exactly to backend (Client.Address) field names
const ADDRESS_FIELDS = [
  { key: 'streetLine1', label: 'Street Line 1' },
  { key: 'streetLine2', label: 'Street Line 2' },
  { key: 'city',        label: 'City' },
  { key: 'state',       label: 'State' },
  { key: 'country',     label: 'Country' },
  { key: 'zipcode',     label: 'Zipcode' },
];

const isAddressField = (fieldValue) =>
  fieldValue === 'billingAddress' || fieldValue === 'shippingAddress';

const selectStyles = {
  control: (base, state) => ({
    ...base,
    borderRadius: '10px',
    borderColor: state.isFocused ? '#f59e0b' : '#e5e7eb',
    boxShadow: state.isFocused ? '0 0 0 2px #fef3c7' : 'none',
    fontSize: '14px',
    minHeight: '42px',
    '&:hover': { borderColor: '#f59e0b' },
  }),
  menu: (base) => ({ ...base, borderRadius: '10px', overflow: 'hidden', zIndex: 30 }),
  option: (base, state) => ({
    ...base,
    fontSize: '14px',
    backgroundColor: state.isSelected ? '#f59e0b' : state.isFocused ? '#fef3c7' : 'white',
    color: state.isSelected ? 'white' : '#374151',
  }),
};

const inputClass =
  'w-full px-3 py-2 text-sm border border-gray-200 rounded-[10px] focus:outline-none focus:border-yellow-400 focus:ring-2 focus:ring-yellow-100 transition-all';

const EditClientModal = ({ selectedItems = [], clientIds = [], onClose, onSave }) => {
  const resolvedIds = clientIds.length > 0 ? clientIds : selectedItems.map(c => c.id);
  const count = resolvedIds.length;

  const [selectedField, setSelectedField] = useState(null);
  const [selectedValue, setSelectedValue] = useState(null);

  // ✅ Address sub-fields state — keys match backend field names exactly
  const [addressValues, setAddressValues] = useState({
    streetLine1: '',
    streetLine2: '',
    city: '',
    state: '',
    country: '',
    zipcode: '',
  });

  const handleFieldChange = (opt) => {
    setSelectedField(opt);
    setSelectedValue(null);
    setAddressValues({
      streetLine1: '',
      streetLine2: '',
      city: '',
      state: '',
      country: '',
      zipcode: '',
    });
  };

  const handleAddressChange = (key, value) => {
    setAddressValues(prev => ({ ...prev, [key]: value }));
  };

  // Address is valid if at least one sub-field is filled
  const addressHasValue = Object.values(addressValues).some(v => v.trim() !== '');

  const isValid =
    selectedField &&
    resolvedIds.length > 0 &&
    (isAddressField(selectedField.value) ? addressHasValue : !!selectedValue);

  const handleSave = () => {
    if (!isValid) return;

    if (isAddressField(selectedField.value)) {
      // ✅ Send address as object — only non-empty fields included
      const filteredAddress = Object.fromEntries(
        Object.entries(addressValues).filter(([, v]) => v.trim() !== '')
      );
      onSave({
        ids: resolvedIds,
        fieldName: selectedField.value,   // "billingAddress" or "shippingAddress"
        fieldValue: filteredAddress,       // { streetLine1, city, ... }
      });
    } else {
      onSave({
        ids: resolvedIds,
        fieldName: selectedField.value,
        fieldValue: selectedValue.value,
      });
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />

      {/* Modal */}
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden">
        {/* Top accent bar */}
        <div className="h-1.5 w-full bg-gradient-to-r from-yellow-400 via-orange-400 to-yellow-500" />

        {/* Header */}
        <div className="flex items-start justify-between px-6 pt-6 pb-2">
          <div className="flex items-center gap-3">
            <div className="flex-shrink-0 w-11 h-11 rounded-full bg-yellow-50 flex items-center justify-center border border-yellow-100">
              <Pencil size={20} className="text-yellow-500" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900 tracking-tight">
                Bulk Edit {count > 1 ? 'Clients' : 'Client'}
              </h2>
              <p className="text-xs text-gray-400 mt-0.5">
                {count} {count === 1 ? 'client' : 'clients'} selected
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
          >
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-5 space-y-4 max-h-[70vh] overflow-y-auto">
          <p className="text-xs text-gray-500">
            Select a field and the new value to apply to all selected clients.
          </p>

          {/* Field selector */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Field to update
            </label>
            <Select
              options={FIELD_OPTIONS}
              value={selectedField}
              onChange={handleFieldChange}
              placeholder="Select a field..."
              styles={selectStyles}
              isClearable={false}
            />
          </div>

          {/* ✅ Address sub-fields — shown when billingAddress or shippingAddress is selected */}
          {selectedField && isAddressField(selectedField.value) ? (
            <div className="space-y-3">
              <label className="block text-sm font-medium text-gray-700">
                {selectedField.label} Fields
              </label>
              {ADDRESS_FIELDS.map(({ key, label }) => (
                <div key={key}>
                  <label className="block text-xs text-gray-500 mb-1">{label}</label>
                  <input
                    type="text"
                    className={inputClass}
                    placeholder={`Enter ${label}`}
                    value={addressValues[key]}
                    onChange={(e) => handleAddressChange(key, e.target.value)}
                  />
                </div>
              ))}
            </div>
          ) : (
            /* Enum value selector — for all other fields */
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                New value
              </label>
              <Select
                options={selectedField ? FIELD_VALUES[selectedField.value] || [] : []}
                value={selectedValue}
                onChange={setSelectedValue}
                placeholder={selectedField ? 'Select a value...' : 'Select a field first'}
                isDisabled={!selectedField}
                styles={selectStyles}
                isClearable={false}
              />
            </div>
          )}

          {/* Preview chip */}
          {isValid && (
            <div className="flex items-center gap-2 px-3 py-2.5 bg-yellow-50 border border-yellow-200 rounded-xl">
              <span className="text-xs text-yellow-800">
                Will set{' '}
                <span className="font-bold">{selectedField.label}</span>
                {isAddressField(selectedField.value) ? (
                  <span>
                    {' → '}
                    <span className="font-bold">
                      {Object.entries(addressValues)
                        .filter(([, v]) => v.trim())
                        .map(([k, v]) => `${k}: ${v}`)
                        .join(', ')}
                    </span>
                  </span>
                ) : (
                  <span>
                    {' → '}
                    <span className="font-bold">{selectedValue?.label}</span>
                  </span>
                )}
              </span>
              <span className="ml-auto text-xs text-yellow-500 whitespace-nowrap">
                for {count} client{count !== 1 ? 's' : ''}
              </span>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 bg-gray-50 border-t border-gray-100">
          <button
            onClick={onClose}
            className="px-5 py-2.5 text-sm font-medium text-gray-600 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 hover:border-gray-300 transition-all"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={!isValid}
            className="flex items-center gap-2 px-5 py-2.5 text-sm font-semibold text-white bg-yellow-500 rounded-xl hover:bg-yellow-600 active:scale-95 transition-all shadow-sm shadow-yellow-200 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Pencil size={15} />
            Save Changes
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditClientModal;